import { Menu } from '@jwp/ott-common/types/config';
import { persist, StateStorage } from 'zustand/middleware';
import type { StateCreator } from 'zustand';

import { createStore } from './utils';

export type PaginationState = {
  pageLimit: number;
  pageOffset: number;
  nextPageLimit: number;
  nextPageOffset: number;
  totalLoaded: number;
  hasNext?: boolean;
  lastFetched?: number;
};

export type PageState = PaginationState & {
  scrollPosition: number;
  from?: string;
};

export type RowState = PaginationState & {
  page: number;
  focusedIndex: number;
};

export type Home = {
  rowsToLoad: number;
};

type CardGrid = {
  title: string;
  rowsToLoad: number;
};

export type UIStore = {
  pages: Record<string, PageState>;
  rows: Record<string, RowState>;
  Home: Home;
  CardGrid: CardGrid;
  setPageState: (id: string, state: Partial<PageState>) => void;
  resetPage: (id: string) => void;
  setRowState: (pathname: string, id: string, state: Partial<RowState>) => void;
  resetRow: (pathname: string, id: string) => void;
  resetAll: () => void;
  activeScreen: string;
  click: Menu;
};

export const getScopedId = (pathname: string, id: string) => `${pathname}:${id}`;

const uiStoreCreator: StateCreator<UIStore> = persist(
  (set: any, get: any) => ({
    pages: {},
    rows: {},
    Home: { rowsToLoad: 6 },
    CardGrid: { title: '', rowsToLoad: 6 },
    click: { label: 'Home', contentId: '' },
    activeScreen: '',

    setPageState: (id: string, state: Partial<PageState>) =>
      set((s: any) => {
        const defaults: PageState = {
          scrollPosition: 0,
          pageLimit: 25,
          pageOffset: 1,
          nextPageLimit: 0,
          nextPageOffset: 0,
          totalLoaded: 0,
          from: '',
        };
        return {
          pages: {
            ...s.pages,
            [id]: {
              ...defaults,
              ...s.pages[id],
              ...state,
            },
          },
        };
      }),

    setRowState: (pathname: string, id: string, state: Partial<PageState>) =>
      set((s: any) => {
        const scopedId = getScopedId(pathname, id);
        const defaults: RowState = {
          page: 0,
          focusedIndex: 0,
          pageLimit: 25,
          pageOffset: 1,
          nextPageLimit: 0,
          nextPageOffset: 0,
          totalLoaded: 0,
        };
        return {
          rows: {
            ...s.rows,
            [scopedId]: {
              ...defaults,
              ...s.rows[scopedId],
              ...state,
            },
          },
        };
      }),

    resetPage: (id: string) =>
      set((s: any) => {
        const { [id]: _, ...rest } = s.pages;
        return { pages: rest };
      }),

    resetRow: (pathname: string, id: string) =>
      set((s: any) => {
        const scopedId = getScopedId(pathname, id);
        const { [scopedId]: _, ...rest } = s.rows;
        return { rows: rest };
      }),

    resetAll: () =>
      set(() => ({
        pages: {},
        rows: {},
        Home: { rowsToLoad: 6 },
        CardGrid: { title: '', rowsToLoad: 6 },
      })),
  }),
  {
    name: 'ui-store',
    getStorage: (): StateStorage => sessionStorage,
    partialize: (state) => ({ click: state.click, activeScreen: state.activeScreen }),
  },
) as unknown as StateCreator<UIStore>;

export const useContentStateStore = createStore<UIStore>('UIStore', uiStoreCreator);
